import React, { useMemo, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, ChevronDown, Layers, Globe2, ExternalLink } from 'lucide-react';
import { ContentBadge } from './shared';

const LANGUAGES = [
  { code: 'fr', label: 'Français', short: 'FR' },
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'ar', label: 'العربية', short: 'AR' },
];

const formatLangLabel = (code) => LANGUAGES.find((l) => l.code === code)?.label || code.toUpperCase();

/**
 * TafsirPanel
 *
 * Renders the multi-source, multilingual tafsir returned by the backend
 * (/api/quran/verses/{s}/{v} → field `tafsir`). Each entry contains:
 *   { source: { slug, display_name, display_name_localized, author, language_code, ...},
 *     language_code, group: { ayah_keys, span_size, ... }, text, text_plain }
 *
 * Design rules (per product spec):
 *  - Clear visual separation between Quran text, translation, and tafsir.
 *  - Explicit source + author shown on every entry; never mix editorial summaries
 *    with source tafsir. Al-Mukhtasar itself is an abridged/editorial work but is
 *    already published as such — we surface that via the source label, not hide it.
 *  - Language switcher is the primary control (3 langs initially, scalable).
 *  - If multiple sources per language, a secondary selector appears automatically.
 *  - Multi-ayah groups are called out ("Couvre les versets X–Y") so the user
 *    knows the tafsir applies to a verse range.
 *
 * The component is purely presentational — data is fetched by the parent.
 */
export const TafsirPanel = ({
  items = [],               // array of tafsir entries
  legacyEditorial = [],     // legacy curated tafsirs (e.g. Al-Fatiha Ibn Kathir extracts)
  defaultLanguage = 'fr',
  preferredSourceSlugs = [],
  verseKey,
}) => {
  // Group tafsir entries by language
  const byLanguage = useMemo(() => {
    const map = {};
    for (const t of items) {
      (map[t.language_code] ||= []).push(t);
    }
    // Reorder items inside each language so preferred sources come first
    if (preferredSourceSlugs.length) {
      const order = new Map(preferredSourceSlugs.map((slug, i) => [slug, i]));
      for (const lang of Object.keys(map)) {
        map[lang].sort((a, b) => {
          const aIdx = order.has(a.source.slug) ? order.get(a.source.slug) : 1e6;
          const bIdx = order.has(b.source.slug) ? order.get(b.source.slug) : 1e6;
          if (aIdx !== bIdx) return aIdx - bIdx;
          return (a.source.sort_order || 999) - (b.source.sort_order || 999);
        });
      }
    }
    return map;
  }, [items, preferredSourceSlugs]);

  const availableLangs = useMemo(
    () => LANGUAGES.filter((l) => byLanguage[l.code]?.length > 0),
    [byLanguage],
  );

  const [activeLang, setActiveLang] = useState(defaultLanguage);
  useEffect(() => {
    // If defaultLanguage isn't available, fallback to first available
    if (!byLanguage[activeLang] && availableLangs[0]) {
      setActiveLang(availableLangs[0].code);
    }
  }, [byLanguage, availableLangs, activeLang]);

  const [activeSourceSlug, setActiveSourceSlug] = useState(null);
  useEffect(() => {
    // Reset source when language changes; default to first source of that language
    const langItems = byLanguage[activeLang] || [];
    if (langItems.length) setActiveSourceSlug(langItems[0].source.slug);
  }, [activeLang, byLanguage]);

  // No tafsir data at all and no legacy editorial either → empty state
  const hasAnyTafsir = items.length > 0;
  const hasAnyLegacy = legacyEditorial.length > 0;
  if (!hasAnyTafsir && !hasAnyLegacy) {
    return (
      <p className="text-sm text-text-muted text-center py-6" data-testid="tafsir-empty">
        Pas de tafsir disponible pour ce verset
      </p>
    );
  }

  const langItems = byLanguage[activeLang] || [];
  const activeSources = langItems.map((t) => t.source);
  const activeItem = langItems.find((t) => t.source.slug === activeSourceSlug) || langItems[0];

  return (
    <div className="space-y-4" data-testid="tafsir-panel">
      {hasAnyTafsir && (
        <>
          {/* Language switcher */}
          {availableLangs.length > 1 && (
            <div className="flex items-center gap-2 flex-wrap" data-testid="tafsir-lang-switcher">
              <Globe2 className="w-3.5 h-3.5 text-text-muted" />
              {availableLangs.map((l) => (
                <button
                  key={l.code}
                  onClick={() => setActiveLang(l.code)}
                  data-testid={`tafsir-lang-${l.code}`}
                  className={`text-xs px-2.5 py-1 rounded-full transition-all ${
                    activeLang === l.code
                      ? 'bg-primary/20 text-primary border border-primary/40'
                      : 'bg-surface-elevated text-text-muted border border-transparent hover:text-text-primary'
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          )}

          {/* Source selector (only if several sources for this language) */}
          {activeSources.length > 1 && (
            <div className="flex items-center gap-2 flex-wrap" data-testid="tafsir-source-switcher">
              <Layers className="w-3.5 h-3.5 text-text-muted" />
              {activeSources.map((s) => (
                <button
                  key={s.slug}
                  onClick={() => setActiveSourceSlug(s.slug)}
                  data-testid={`tafsir-source-${s.slug}`}
                  className={`text-xs px-2.5 py-1 rounded transition-all ${
                    (activeItem?.source.slug === s.slug)
                      ? 'bg-primary/15 text-primary border border-primary/30'
                      : 'bg-surface-elevated text-text-muted border border-transparent hover:text-text-primary'
                  }`}
                >
                  {s.display_name_localized?.[activeLang] || s.display_name}
                </button>
              ))}
            </div>
          )}

          {/* Active tafsir entry */}
          <AnimatePresence mode="wait">
            {activeItem && (
              <motion.article
                key={activeItem.source.slug + '-' + activeItem.language_code}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.18 }}
                className="p-4 bg-surface-elevated rounded-lg border-l-2 border-primary/60"
                data-testid="tafsir-active-entry"
              >
                {/* Entry header: source + author */}
                <div className="flex items-start justify-between gap-2 mb-3 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    <ContentBadge type="tafsir" />
                    <span className="text-xs font-medium text-primary">
                      {activeItem.source.display_name_localized?.[activeLang] ||
                        activeItem.source.display_name}
                    </span>
                  </div>
                  <span className="text-[10px] uppercase tracking-wider text-text-muted">
                    {formatLangLabel(activeItem.language_code)}
                  </span>
                </div>

                {/* Multi-ayah badge (when tafsir covers a range) */}
                {activeItem.group.span_size > 1 && (
                  <div
                    className="mb-3 text-[11px] px-2 py-1 rounded bg-amber-500/10 text-amber-300 border border-amber-500/20 inline-flex items-center gap-1.5"
                    data-testid="tafsir-multi-ayah-badge"
                  >
                    <BookOpen className="w-3 h-3" />
                    Couvre les versets {activeItem.group.from_surah}:{activeItem.group.from_ayah}
                    &nbsp;→&nbsp;{activeItem.group.to_surah}:{activeItem.group.to_ayah}
                  </div>
                )}

                {/* Tafsir body — render raw text (may include <b>, <i> etc.) */}
                <div
                  className={`text-[15px] text-text-secondary leading-[1.9] tafsir-body ${
                    activeItem.language_code === 'ar' ? 'text-right font-arabic text-lg' : ''
                  }`}
                  dir={activeItem.language_code === 'ar' ? 'rtl' : 'ltr'}
                  /* eslint-disable-next-line react/no-danger */
                  dangerouslySetInnerHTML={{ __html: activeItem.text }}
                />

                {/* Entry footer: author + provenance */}
                <div className="mt-4 pt-3 border-t border-border/50 flex items-center justify-between gap-2 flex-wrap text-xs">
                  <p className="text-text-muted">
                    {activeItem.source.author}
                  </p>
                  {activeItem.source.qul_resource_id && (
                    <a
                      href={`https://qul.tarteel.ai/resources/tafsir/${activeItem.source.qul_resource_id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-text-muted hover:text-primary flex items-center gap-1"
                    >
                      Source QUL
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </motion.article>
            )}
          </AnimatePresence>
        </>
      )}

      {/* Legacy editorial extracts (kept for Al-Fatiha curated Ibn Kathir / Qurtubi / etc.) */}
      {hasAnyLegacy && (
        <LegacyTafsirSection items={legacyEditorial} />
      )}
    </div>
  );
};

const LegacyTafsirSection = ({ items }) => {
  const [open, setOpen] = useState(false);
  return (
    <section className="mt-6" data-testid="tafsir-editorial-section">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between text-left px-3 py-2 rounded-lg bg-surface-elevated/60 hover:bg-surface-elevated transition-colors"
      >
        <div className="flex items-center gap-2">
          <ContentBadge type="scholarly" />
          <span className="text-xs font-medium text-text-primary">
            Extraits savants classiques ({items.length})
          </span>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-text-muted transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="pt-3 space-y-3">
              {items.map((t, i) => (
                <div
                  key={i}
                  className="p-3 bg-surface-elevated/40 rounded-lg border-l-2 border-blue-500/40"
                >
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <span className="text-xs font-medium text-blue-300">
                      {t.source_name}
                    </span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-900/30 text-blue-300">
                      Extrait éditorial
                    </span>
                  </div>
                  <p className="text-sm text-text-secondary leading-relaxed whitespace-pre-wrap">
                    {t.content}
                  </p>
                  {t.author && (
                    <p className="text-xs text-text-muted mt-2">— {t.author}</p>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default TafsirPanel;
